import { useNavigate } from "@tanstack/react-router";
import {
  Add,
  Button,
  Divider,
  Drawer,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { TidLinkedAccountItem } from "@/api/graphql/billing/searchTidLinkedAccounts/types";
import { AddTidLinkedAccountDrawerContext } from "@/components/Accounts/details/tid/shared/add/config.ts";
import {
  StyledAccountsDetailsTidContainerDiv,
  StyledAccountsDetailsTidHeader,
  StyledAddButtonRow,
  StyledCountText,
  StyledSortableCustomTable,
  StyledTableContainerDiv,
} from "@/components/Accounts/details/tid/shared/styles";
import { Loading } from "@/components/shared/Loading/component";

import { AddTidLinkedAccountDrawer } from "../shared/add/component";
import { DeleteTidLinkedAccountDrawer } from "../shared/delete/component";
import { useTidLinkedAccounts } from "../shared/hooks";
import { fromTidAccountsToColumns, fromTidAccountsToRows } from "./mappers";
import type { AccountsDetailsTidPayerProps } from "./types";

export const AccountsDetailsTidPayer = ({
  accountId,
  getTidLinkedAccountsFn,
  createTidLinkedAccountFn,
  deleteTidLinkedAccountFn,
  getAccountsLinkableToTidFn,
  typeIds,
  customTypeId,
  isLoadingTypes,
}: Readonly<AccountsDetailsTidPayerProps>) => {
  const navigate = useNavigate();
  const { i18n, t: tCompanies } = useTranslation("companies");

  const [selectedAccount, setSelectedAccount] =
    useState<TidLinkedAccountItem | null>(null);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useTidLinkedAccounts({
      getTidLinkedAccountsFn,
      accountId,
      typeIds,
      enabled: !isLoadingTypes && typeIds.length > 0,
    });

  const items = data.items || [];
  const total = data.pagination.total || 0;
  const remaining = Math.max(0, total - data.items.length);
  const nextPageCount = Math.min(data.pagination.size, remaining);

  const columns = fromTidAccountsToColumns({
    items,
    tCompanies,
  });

  const rows = fromTidAccountsToRows({
    items,
    tCompanies,
    locale: i18n.language,
    navigate,
    onDelete: (id) =>
      setSelectedAccount(
        items.find((item) => item.linkedAccountId === id) || null,
      ),
  });

  return (
    <StyledAccountsDetailsTidContainerDiv>
      <StyledAccountsDetailsTidHeader>
        <Text variant='title2'>{tCompanies("companies.tid.payer.title")}</Text>
        <Divider />
      </StyledAccountsDetailsTidHeader>

      <StyledAddButtonRow>
        <Button
          variant='primary'
          isLowercase
          leftIcon={<Add width={24} height={24} color='#FFFFFF' />}
          buttonText={tCompanies("companies.tid.payer.actions.add")}
          dataTestId='accounts__account-details-tid-payer_add-account-button'
          onClick={() => setIsAddDrawerOpen(true)}
        />
      </StyledAddButtonRow>

      <Loading isLoading={isLoading}>
        <StyledTableContainerDiv>
          <StyledSortableCustomTable
            canSelectRows={false}
            rows={rows}
            columns={columns}
            loadMoreButton={{
              label: tCompanies("companies.tid.payer.table.loadMore", {
                count: nextPageCount,
              }),
              onClick: () => {
                fetchNextPage();
              },
              isDisabled: items.length >= total || isFetchingNextPage,
              dataTestId:
                "accounts__account-details-tid-payer_load-more-button",
            }}
          />
          <StyledCountText variant='sub3'>
            {tCompanies("companies.tid.payer.table.count", {
              count: items.length,
              total,
            })}
          </StyledCountText>
        </StyledTableContainerDiv>
      </Loading>

      <Drawer
        isOpen={selectedAccount !== null}
        onClose={() => setSelectedAccount(null)}
        width='520px'
      >
        <DeleteTidLinkedAccountDrawer
          titleKey='companies.tid.drawer.payer.title'
          dataTestIdPrefix='tid-payer'
          deleteTidLinkedAccountFn={deleteTidLinkedAccountFn}
          onClose={() => setSelectedAccount(null)}
          selectedAccount={selectedAccount}
        />
      </Drawer>

      <Drawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
        width='520px'
      >
        <AddTidLinkedAccountDrawer
          accountId={accountId}
          context={AddTidLinkedAccountDrawerContext.payer}
          typeId={customTypeId}
          createTidLinkedAccountFn={createTidLinkedAccountFn}
          getAccountsLinkableToTidFn={getAccountsLinkableToTidFn}
          onClose={() => setIsAddDrawerOpen(false)}
        />
      </Drawer>
    </StyledAccountsDetailsTidContainerDiv>
  );
};
